/**
 * KnowledgeBaseTab - Gestión de la Base de Conocimiento
 * Diseño: Light theme matching AdminCorrections (white cards, dark text, gradient bars)
 */

import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Switch,
  Alert,
  Collapse,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  MedicalServices,
  Payments,
  Schedule,
  LocationOn,
  Policy,
  Help,
  Groups,
  LocalOffer,
  Healing,
  Storage as StorageIcon,
} from "@mui/icons-material";

import { StyledDialog, inputStyles } from "../../common";

const API_URL = process.env.REACT_APP_API_URL || "https://turismo.nara.chat/api";

const CATEGORY_ICONS = {
  sintomas: <Healing sx={{ fontSize: 16 }} />,
  servicios: <MedicalServices sx={{ fontSize: 16 }} />,
  precios: <Payments sx={{ fontSize: 16 }} />,
  horarios: <Schedule sx={{ fontSize: 16 }} />,
  ubicacion: <LocationOn sx={{ fontSize: 16 }} />,
  politicas: <Policy sx={{ fontSize: 16 }} />,
  faq: <Help sx={{ fontSize: 16 }} />,
  personal: <Groups sx={{ fontSize: 16 }} />,
  promociones: <LocalOffer sx={{ fontSize: 16 }} />,
};

// Category color map
const CATEGORY_COLORS = {
  servicios: { gradient: "linear-gradient(90deg, #8b5cf6, #6366f1)", border: "#8b5cf6" },
  precios: { gradient: "linear-gradient(90deg, #22c55e, #16a34a)", border: "#22c55e" },
  horarios: { gradient: "linear-gradient(90deg, #3b82f6, #2563eb)", border: "#3b82f6" },
  ubicacion: { gradient: "linear-gradient(90deg, #ef4444, #dc2626)", border: "#ef4444" },
  politicas: { gradient: "linear-gradient(90deg, #64748b, #475569)", border: "#64748b" },
  faq: { gradient: "linear-gradient(90deg, #06b6d4, #0891b2)", border: "#06b6d4" },
  personal: { gradient: "linear-gradient(90deg, #ec4899, #db2777)", border: "#ec4899" },
  promociones: { gradient: "linear-gradient(90deg, #f97316, #ea580c)", border: "#f97316" },
  sintomas: { gradient: "linear-gradient(90deg, #f59e0b, #d97706)", border: "#f59e0b" },
};

// Light theme card style (matching AdminCorrections)
const cardSx = {
  borderRadius: 3,
  background: "rgba(255, 255, 255, 0.85)",
  backdropFilter: "blur(20px)",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
  border: "1px solid rgba(255, 255, 255, 0.5)",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 8px 28px rgba(0, 0, 0, 0.12)",
  },
};

export default function KnowledgeBaseTab({ chatbotId, entries, categories, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    category: "servicios",
    title: "",
    content: "",
    keywords: "",
    priority: 0,
    enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Agrupar entradas por categoría
  const entriesByCategory = entries.reduce((acc, entry) => {
    if (!acc[entry.category]) acc[entry.category] = [];
    acc[entry.category].push(entry);
    return acc;
  }, {});

  const toggleCategory = (catId) => {
    setExpandedCategories((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  const openCreateDialog = () => {
    setEditingEntry(null);
    setFormData({
      category: "servicios",
      title: "",
      content: "",
      keywords: "",
      priority: 0,
      enabled: true,
    });
    setError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      category: entry.category,
      title: entry.title,
      content: entry.content,
      keywords: entry.keywords || "",
      priority: entry.priority,
      enabled: entry.enabled,
    });
    setError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const url = editingEntry
        ? `${API_URL}/smart-flows/${chatbotId}/knowledge/${editingEntry.id}`
        : `${API_URL}/smart-flows/${chatbotId}/knowledge`;

      const response = await fetch(url, {
        method: editingEntry ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Error guardando entrada");
      }

      setDialogOpen(false);
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("¿Eliminar esta entrada?")) return;

    try {
      const response = await fetch(
        `${API_URL}/smart-flows/${chatbotId}/knowledge/${entryId}`,
        { method: "DELETE" }
      );
      if (!response.ok) throw new Error("Error eliminando entrada");
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  const getCatColor = (catId) => CATEGORY_COLORS[catId] || { gradient: "linear-gradient(90deg, #64748b, #475569)", border: "#64748b" };

  return (
    <Box>
      {/* Header compacto */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography sx={{ color: "#1e293b", fontWeight: 800, fontSize: 18 }}>
            Base de Conocimiento
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: 12 }}>
            {entries.length} entradas · {Object.keys(entriesByCategory).length} categorías
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{
            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
            boxShadow: "0 4px 14px rgba(59, 130, 246, 0.4)",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 12,
            borderRadius: 2,
            py: 0.75,
            "&:hover": {
              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              boxShadow: "0 6px 20px rgba(59, 130, 246, 0.5)",
            },
          }}
        >
          Nueva entrada
        </Button>
      </Box>

      {/* Lista de entradas por categoría */}
      {entries.length === 0 ? (
        <Box
          sx={{
            ...cardSx,
            py: 5,
            textAlign: "center",
            border: "1px dashed rgba(0,0,0,0.12)",
          }}
        >
          <StorageIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1.5 }} />
          <Typography sx={{ color: "#64748b", fontSize: 14, fontWeight: 600, mb: 1 }}>
            No hay entradas en la base de conocimiento
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            sx={{
              color: "#3b82f6",
              borderColor: "rgba(59, 130, 246, 0.4)",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              borderRadius: 2,
              "&:hover": { borderColor: "#3b82f6", bgcolor: "rgba(59, 130, 246, 0.04)" },
            }}
          >
            Agregar primera entrada
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {categories.map((cat) => {
            const catEntries = entriesByCategory[cat.id] || [];
            if (catEntries.length === 0) return null;

            const isExpanded = expandedCategories[cat.id] !== false; // Default expanded
            const catColor = getCatColor(cat.id);

            return (
              <Box
                key={cat.id}
                sx={{
                  ...cardSx,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: catColor.gradient,
                  },
                }}
              >
                {/* Category header */}
                <Box
                  onClick={() => toggleCategory(cat.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1.25,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                  }}
                >
                  {/* Category icon with gradient background */}
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      background: catColor.gradient,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      boxShadow: `0 4px 12px ${catColor.border}40`,
                      flexShrink: 0,
                      "& svg": { fontSize: 16 },
                    }}
                  >
                    {CATEGORY_ICONS[cat.id] || <Help sx={{ fontSize: 16 }} />}
                  </Box>
                  <Typography sx={{ color: "#1e293b", fontWeight: 700, fontSize: 14, flex: 1 }}>
                    {cat.name}
                  </Typography>
                  <Chip
                    label={catEntries.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: `${catColor.border}15`,
                      color: catColor.border,
                    }}
                  />
                  {isExpanded ? (
                    <CollapseIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  ) : (
                    <ExpandIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  )}
                </Box>

                {/* Entries with Collapse */}
                <Collapse in={isExpanded} timeout={250}>
                  <Box sx={{ px: 1.5, pb: 1.5 }}>
                    {catEntries.map((entry) => (
                      <Box
                        key={entry.id}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          px: 1.5,
                          py: 1,
                          mb: 0.5,
                          borderRadius: 2,
                          bgcolor: entry.enabled ? "#f8fafc" : "rgba(239, 68, 68, 0.04)",
                          border: entry.enabled
                            ? "1px solid #e2e8f0"
                            : "1px solid rgba(239, 68, 68, 0.15)",
                          opacity: entry.enabled ? 1 : 0.7,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: entry.enabled ? "#f1f5f9" : "rgba(239, 68, 68, 0.06)",
                            transform: "translateX(4px)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: "#1e293b",
                              fontWeight: 600,
                              fontSize: 13,
                              mb: 0.25,
                            }}
                          >
                            {entry.title}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#64748b",
                              fontSize: 12,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: 1.4,
                            }}
                          >
                            {entry.content}
                          </Typography>
                          {entry.keywords && (
                            <Box sx={{ display: "flex", gap: 0.5, mt: 0.75, flexWrap: "wrap" }}>
                              {entry.keywords
                                .split(",")
                                .slice(0, 3)
                                .map((kw, i) => (
                                  <Chip
                                    key={i}
                                    label={kw.trim()}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: 10,
                                      fontWeight: 600,
                                      bgcolor: `${catColor.border}12`,
                                      color: catColor.border,
                                    }}
                                  />
                                ))}
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ display: "flex", gap: 0.25 }}>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(entry)}
                              sx={{ color: "#94a3b8", p: 0.5, "&:hover": { color: "#3b82f6" } }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(entry.id)}
                              sx={{ color: "#94a3b8", p: 0.5, "&:hover": { color: "#ef4444" } }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Dialog de crear/editar → StyledDialog */}
      <StyledDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editingEntry ? "Editar entrada" : "Nueva entrada"}
        icon={<StorageIcon />}
        variant="compact"
        maxWidth="sm"
      >
        <StyledDialog.Body compact>
          {error && (
            <Alert severity="error" sx={{ py: 0.5, fontSize: 12 }}>
              {error}
            </Alert>
          )}

          {/* Row 1: Categoría + Habilitado */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, color: "#1e3a5f", fontWeight: 600, mb: 0.5 }}>
                Categoría
              </Typography>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                size="small"
                fullWidth
                sx={{
                  borderRadius: 2,
                  bgcolor: "#fff",
                  fontSize: 13,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#1e3a5f" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e3a5f" },
                }}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ color: getCatColor(cat.id).border }}>{CATEGORY_ICONS[cat.id]}</Box>
                      {cat.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-end", pb: 0.5 }}>
              <Box
                onClick={() => setFormData({ ...formData, enabled: !formData.enabled })}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  cursor: "pointer",
                  bgcolor: formData.enabled ? "rgba(34, 197, 94, 0.08)" : "#f1f5f9",
                  border: formData.enabled
                    ? "1px solid rgba(34, 197, 94, 0.3)"
                    : "1px solid #e2e8f0",
                }}
              >
                <Switch
                  checked={formData.enabled}
                  size="small"
                  sx={{
                    width: 28,
                    height: 16,
                    p: 0,
                    "& .MuiSwitch-switchBase": {
                      p: 0.25,
                      "&.Mui-checked": {
                        transform: "translateX(12px)",
                        color: "#22c55e",
                        "& + .MuiSwitch-track": { bgcolor: "#22c55e", opacity: 0.5 },
                      },
                    },
                    "& .MuiSwitch-thumb": { width: 12, height: 12 },
                    "& .MuiSwitch-track": { borderRadius: 8, bgcolor: "#cbd5e1" },
                  }}
                />
                <Typography sx={{ fontSize: 12, color: formData.enabled ? "#16a34a" : "#64748b" }}>
                  {formData.enabled ? "Activa" : "Inactiva"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Row 2: Título */}
          <TextField
            fullWidth
            size="small"
            label="Título"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Ej: Horario de atención"
            sx={inputStyles}
          />

          {/* Row 3: Contenido */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Contenido"
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            placeholder="Información que el asistente usará para responder..."
            sx={inputStyles}
          />

          {/* Row 4: Keywords */}
          <Box>
            <TextField
              fullWidth
              size="small"
              label="Palabras clave (opcional)"
              value={formData.keywords}
              onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
              placeholder="horario, atención, horas (separadas por coma)"
              sx={inputStyles}
            />
            <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>
              Ayudan a encontrar esta entrada más rápido
            </Typography>
          </Box>
        </StyledDialog.Body>

        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setDialogOpen(false)} />
          <StyledDialog.PrimaryButton
            fullWidth
            onClick={handleSave}
            disabled={!formData.title || !formData.content}
            loading={saving}
          >
            Guardar
          </StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>
    </Box>
  );
}
